import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './StudentActions.css';

function Classes() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [error, setError] = useState(null);
  const { selectedBranchId, handleBranchChange, refreshTrigger } = useBranchFilter();
  const [editingClass, setEditingClass] = useState(null);
  const [editForm, setEditForm] = useState({
    gradeId: '',
    streamId: '',
    academicYear: new Date().getFullYear().toString(),
    term: '',
    sections: [],
    subjects: [''],
    teachers: [''],
    subjectSections: [''],
    classTeacherId: '',
    assistantTeacherId: ''
  });
  
  // For edit form - curriculum data
  const [editAvailableSubjects, setEditAvailableSubjects] = useState([]);
  const [editFilteredTeachers, setEditFilteredTeachers] = useState([]);
  const [editErrors, setEditErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  // Filters/search/sort/pagination additions
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterAcademicYear, setFilterAcademicYear] = useState('all');
  const [filterTerm, setFilterTerm] = useState('all');
  const [filterTeacherStatus, setFilterTeacherStatus] = useState('all'); // all | active | inactive
  const [sortKey, setSortKey] = useState('class'); // class | grade | academic_year | term | teacher
  const [sortDir, setSortDir] = useState('asc');
  
  // Grades and streams for display
  const [grades, setGrades] = useState([]);
  const [streams, setStreams] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [studentCounts, setStudentCounts] = useState({});
  const [todayAttendanceCounts, setTodayAttendanceCounts] = useState({});
  const [selectedClassDetails, setSelectedClassDetails] = useState(null);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showArchiveConfirmPopup, setShowArchiveConfirmPopup] = useState(false);
  const [classToArchive, setClassToArchive] = useState(null);
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [showUpdateSuccessPopup, setShowUpdateSuccessPopup] = useState(false);
  const [updatedClassName, setUpdatedClassName] = useState('');
  const [showArchiveSuccessPopup, setShowArchiveSuccessPopup] = useState(false);
  const [archivedClassName, setArchivedClassName] = useState('');
  const [showRestoreConfirmPopup, setShowRestoreConfirmPopup] = useState(false);
  const [classToRestore, setClassToRestore] = useState(null);
  const [showRestoreSuccessPopup, setShowRestoreSuccessPopup] = useState(false);
  const [restoredClassName, setRestoredClassName] = useState('');
  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] = useState(false);
  const [deletedClassName, setDeletedClassName] = useState('');
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  const fetchClasses = async (branchIdOverride = null) => {
    if (!schoolData?.id) return;
    
    // Get the current branchId - use override, then hook value, then localStorage
    const currentBranchId = branchIdOverride !== null 
      ? branchIdOverride 
      : (selectedBranchId !== undefined 
          ? selectedBranchId 
          : (() => {
              const saved = localStorage.getItem('selectedBranchId');
              return saved && saved !== 'null' ? parseInt(saved) : null;
            })());
    
    try {
      setLoadingClasses(true);
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id);
      
      // Filter by active/inactive status based on view mode
      if (viewMode === 'active') {
        query = query.eq('is_active', true);
      } else {
        query = query.eq('is_active', false);
      }
      
      // Filter by branch - only show classes from the selected branch
      // When a branch is selected, only show classes from that branch
      if (currentBranchId) {
        query = query.eq('branch_id', currentBranchId);
      }
      // If no branch is selected, no additional filter (shows all classes or classes with NULL branch_id)
      
      const { data, error } = await query.order('class_name');
      
      if (error) {
        console.error('Error fetching classes:', error);
        setError('Failed to load classes');
        return;
      }
      
      // Enrich classes with grade and stream names
      // Note: grades and streams are fetched separately, so we enrich after both are loaded
      const enrichedClasses = (data || []).map(cls => {
        const grade = grades.find(g => g.id === cls.grade_id);
        const stream = streams.find(s => s.id === cls.stream_id);
        return {
          ...cls,
          grade: grade || null,
          stream: stream || null
        };
      });
      
      setClasses(enrichedClasses);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load classes');
    } finally {
      setLoadingClasses(false);
    }
  };

  useEffect(() => {
    // Fetch classes for the school
    if (schoolData?.id) {
      fetchClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolData?.id, selectedBranchId, viewMode, refreshTrigger]);

  // Listen for branch change events - force refresh when branch changes
  useEffect(() => {
    const handleBranchChange = (event) => {
      // Get branchId from event detail
      const branchId = event?.detail?.branchId !== undefined ? event.detail.branchId : null;
      
      // Small delay to ensure state has updated, then fetch with the new branchId
      setTimeout(() => {
        if (schoolData?.id) {
          fetchClasses(branchId);
        }
      }, 50);
    };

    window.addEventListener('branchChanged', handleBranchChange);
    return () => {
      window.removeEventListener('branchChanged', handleBranchChange);
    };
  }, [schoolData?.id]);

  useEffect(() => {
    // Fetch teachers for the school
    const fetchTeachers = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingTeachers(true);
        const { data, error } = await supabase
          .from('teachers')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('first_name');
        
        if (error) {
          console.error('Error fetching teachers:', error);
          return;
        }
        
        setTeachers(data || []);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoadingTeachers(false);
      }
    };

    if (schoolData?.id) {
      fetchTeachers();
    }
  }, [schoolData?.id]);

  useEffect(() => {
    // Fetch grades and streams for display
    const fetchGradesAndStreams = async () => {
      if (!schoolData?.id) return;
      
      try {
        // Fetch grades
        let gradesQuery = supabase
          .from('grades')
          .select('*')
          .eq('school_id', schoolData.id);
        
        if (selectedBranchId) {
          gradesQuery = gradesQuery.eq('branch_id', selectedBranchId);
        }
        
        const { data: gradesData } = await gradesQuery.order('name');
        setGrades(gradesData || []);
        
        // Fetch streams
        let streamsQuery = supabase
          .from('streams')
          .select('*')
          .eq('school_id', schoolData.id);
        
        if (selectedBranchId) {
          streamsQuery = streamsQuery.eq('branch_id', selectedBranchId);
        }
        
        const { data: streamsData } = await streamsQuery.order('name');
        setStreams(streamsData || []);
      } catch (err) {
        console.error('Error fetching grades/streams:', err);
      }
    };

    if (schoolData?.id) {
      fetchGradesAndStreams();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (classes.length > 0) {
      fetchCounts();
    }
  }, [classes]);
  
  // Re-enrich classes when grades/streams are loaded or updated
  useEffect(() => {
    if (classes.length > 0 && (grades.length > 0 || streams.length > 0)) {
      setClasses(prevClasses => {
        const enrichedClasses = prevClasses.map(cls => {
          // Always try to enrich with current grades/streams data
          const grade = cls.grade_id ? grades.find(g => g.id === cls.grade_id) : null;
          const stream = cls.stream_id ? streams.find(s => s.id === cls.stream_id) : null;
          
          // Check if enrichment would change anything
          const currentGradeName = cls.grade?.name || null;
          const newGradeName = grade?.name || null;
          const currentStreamName = cls.stream?.name || null;
          const newStreamName = stream?.name || null;
          
          if (currentGradeName !== newGradeName || currentStreamName !== newStreamName) {
            return {
              ...cls,
              grade: grade || null,
              stream: stream || null
            };
          }
          return cls;
        });
        
        // Only return new array if something changed
        const hasChanges = enrichedClasses.some((cls, idx) => {
          const original = prevClasses[idx];
          return (cls.grade?.name !== original.grade?.name) || 
                 (cls.stream?.name !== original.stream?.name);
        });
        
        return hasChanges ? enrichedClasses : prevClasses;
      });
    }
  }, [grades, streams]);

  const fetchCounts = async () => {
    try {
      const classIds = classes.map(c => c.id);
      // Students per class
      const { data: students, error: sErr } = await supabase
        .from('students')
        .select('id,class_id')
        .in('class_id', classIds);
      if (!sErr) {
        const counts = {};
        (students||[]).forEach(s => { counts[s.class_id] = (counts[s.class_id]||0)+1; });
        setStudentCounts(counts);
      }
      // Today attendance present count per class
      const today = new Date().toISOString().split('T')[0];
      const { data: att, error: aErr } = await supabase
        .from('attendance')
        .select('class_id,status,attendance_date')
        .in('class_id', classIds)
        .eq('attendance_date', today);
      if (!aErr) {
        const ac = {};
        (att||[]).forEach(r => {
          if (r.status === 'present') ac[r.class_id] = (ac[r.class_id]||0)+1;
        });
        setTodayAttendanceCounts(ac);
      }
    } catch (e) {
      // ignore counts errors
    }
  };

  const getFilteredSorted = () => {
    let rows = [...classes];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const gradeName = (cls) => cls.grade?.name || '';
      rows = rows.filter(c => 
        (c.class_name||'').toLowerCase().includes(term) || 
        gradeName(c).toLowerCase().includes(term)
      );
    }
    if (filterGrade !== 'all') {
      rows = rows.filter(c => c.grade_id?.toString() === filterGrade);
    }
    if (filterAcademicYear !== 'all') {
      rows = rows.filter(c => c.academic_year?.toString() === filterAcademicYear);
    }
    if (filterTerm !== 'all') {
      rows = rows.filter(c => c.term?.toString() === filterTerm);
    }
    if (filterTeacherStatus !== 'all') {
      const wantActive = filterTeacherStatus === 'active';
      rows = rows.filter(c => {
        const teacher = teachers.find(t => t.id === c.class_teacher_id);
        return teacher ? !!teacher.is_active === wantActive : !wantActive ? true : false;
      });
    }
    rows.sort((a,b)=>{
      let va, vb;
      switch (sortKey) {
        case 'grade': {
          va = a.grade?.name || '';
          vb = b.grade?.name || '';
          break;
        }
        case 'academic_year': {
          va = a.academic_year || 0;
          vb = b.academic_year || 0;
          break;
        }
        case 'term': {
          va = a.term || 0;
          vb = b.term || 0;
          break;
        }
        case 'teacher': {
          const ta = teachers.find(t => t.id===a.class_teacher_id);
          const tb = teachers.find(t => t.id===b.class_teacher_id);
          va = ta ? `${ta.first_name} ${ta.last_name}` : '';
          vb = tb ? `${tb.first_name} ${tb.last_name}` : '';
          break;
        }
        default: va=a.class_name; vb=b.class_name;
      }
      if (va<vb) return sortDir==='asc'?-1:1;
      if (va>vb) return sortDir==='asc'?1:-1;
      return 0;
    });
    return rows;
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  const formatSubjects = (subjects) => {
    if (!subjects || !Array.isArray(subjects)) return 'No subjects';
    return subjects.filter(subject => subject.trim() !== '').join(', ') || 'No subjects';
  };

  const formatTeachers = (teachers) => {
    if (!teachers || !Array.isArray(teachers)) return 'No teachers assigned';
    return teachers.length > 0 ? `${teachers.length} teacher(s) assigned` : 'No teachers assigned';
  };

  const handleViewDetails = async (cls) => {
    try {
      // Fetch full class details with related data
      const { data: classData, error } = await supabase
        .from('classes')
        .select('*')
        .eq('id', cls.id)
        .single();

      if (error) throw error;

      // Enrich with grade and stream data
      const grade = grades.find(g => g.id === classData.grade_id);
      const stream = streams.find(s => s.id === classData.stream_id);

      const enrichedClass = {
        ...classData,
        grade: grade || null,
        stream: stream || null
      };

      setSelectedClassDetails(enrichedClass);
      setShowDetailsPopup(true);
    } catch (error) {
      console.error('Error fetching class details:', error);
      // Fallback to using the class object directly
      const grade = grades.find(g => g.id === cls.grade_id);
      const stream = streams.find(s => s.id === cls.stream_id);
      setSelectedClassDetails({
        ...cls,
        grade: grade || null,
        stream: stream || null
      });
      setShowDetailsPopup(true);
    }
  };

  const handleEditClass = (cls) => {
    console.log('Editing class - Full class object:', cls);
    console.log('Class sections (raw):', cls.sections);
    console.log('Class subjects (raw):', cls.subjects);
    console.log('Class teachers (raw):', cls.teachers);
    console.log('Class subject_sections (raw):', cls.subject_sections);
    
    setEditingClass(cls);
    
    // Ensure sections is an array and has at least one empty string
    let sectionsArray = [''];
    if (cls.sections) {
      if (Array.isArray(cls.sections)) {
        sectionsArray = cls.sections.length > 0 ? cls.sections : [''];
      } else if (typeof cls.sections === 'string') {
        // If it's a string, try to parse it
        try {
          const parsed = JSON.parse(cls.sections);
          sectionsArray = Array.isArray(parsed) && parsed.length > 0 ? parsed : [''];
        } catch (e) {
          sectionsArray = [''];
        }
      }
    }
    
    // Ensure teachers array matches subjects array length
    const subjectsArray = cls.subjects && Array.isArray(cls.subjects) && cls.subjects.length > 0 ? cls.subjects : [''];
    const teachersArray = cls.teachers && Array.isArray(cls.teachers) && cls.teachers.length > 0 ? cls.teachers.map(teacher => teacher.toString()) : [''];
    
    // Parse subject_sections if it's a string
    let subjectSectionsObj = cls.subject_sections;
    if (subjectSectionsObj && typeof subjectSectionsObj === 'string') {
      try {
        subjectSectionsObj = JSON.parse(subjectSectionsObj);
      } catch (e) {
        console.error('Error parsing subject_sections:', e);
        subjectSectionsObj = {};
      }
    }
    
    // Populate subject sections from the subject_sections JSON
    // Support both old format (subject name as key) and new format (subject_index as key)
    // Ensure subjectSections array matches subjects array length
    let subjectSections = [''];
    if (subjectsArray && subjectsArray.length > 0) {
      subjectSections = subjectsArray.map((subject, index) => {
        if (subjectSectionsObj) {
          // Try new format first: subject_index (supports duplicate subjects)
          const newKey = `${subject}_${index}`;
          if (subjectSectionsObj[newKey]) {
            return subjectSectionsObj[newKey];
          }
          // Fallback to old format: subject name as key (for backward compatibility)
          if (subjectSectionsObj[subject]) {
            return subjectSectionsObj[subject];
          }
        }
        return '';
      });
    }
    
    // Pad teachers array to match subjects array length
    const paddedTeachersArray = [];
    for (let i = 0; i < subjectsArray.length; i++) {
      paddedTeachersArray[i] = teachersArray[i] || '';
    }
    
    // Ensure subjectSections array matches subjects array length
    const paddedSubjectSections = [];
    for (let i = 0; i < subjectsArray.length; i++) {
      paddedSubjectSections[i] = subjectSections[i] || '';
    }
    
    console.log('Final form data:');
    console.log('- Sections:', sectionsArray);
    console.log('- Subjects:', subjectsArray);
    console.log('- Teachers:', paddedTeachersArray);
    console.log('- Subject sections:', paddedSubjectSections);
    
    setEditForm({
      gradeId: cls.grade_id ? cls.grade_id.toString() : '',
      streamId: cls.stream_id ? cls.stream_id.toString() : '',
      academicYear: cls.academic_year ? cls.academic_year.toString() : new Date().getFullYear().toString(),
      term: cls.term ? cls.term.toString() : '',
      sections: sectionsArray, // Use the sections array populated from class data
      subjects: subjectsArray,
      teachers: paddedTeachersArray,
      subjectSections: paddedSubjectSections,
      classTeacherId: cls.class_teacher_id ? cls.class_teacher_id.toString() : '',
      assistantTeacherId: cls.assistant_teacher_id ? cls.assistant_teacher_id.toString() : ''
    });
    setEditErrors({});
    
    // Fetch subjects for the selected grade
    if (cls.grade_id) {
      fetchSubjectsForEditGrade(cls.grade_id);
    }
  };
  
  const fetchSubjectsForEditGrade = async (gradeId) => {
    if (!schoolData?.id || !gradeId) return;

    try {
      let subjectsQuery = supabase
        .from('subjects')
        .select('subject_name')
        .eq('grade_id', gradeId);

      if (selectedBranchId) {
        subjectsQuery = subjectsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: subjectsData, error: subjectsError } = await subjectsQuery;

      if (subjectsError) throw subjectsError;
      
      // Get distinct subject names
      const subjectNames = (subjectsData || []).map(s => s.subject_name);
      const distinctSubjects = [...new Set(subjectNames.map(name => name.toLowerCase()))]
        .map(lowerName => {
          return subjectNames.find(name => name.toLowerCase() === lowerName);
        })
        .filter(Boolean)
        .sort();

      setEditAvailableSubjects(distinctSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setEditAvailableSubjects([]);
    }
  };
  
  useEffect(() => {
    // Filter teachers based on teachers assigned in the Subjects & Teachers section
    if (editForm.teachers.length > 0 && teachers.length > 0) {
      // Get all teacher IDs that have been assigned in the Subjects & Teachers section
      const assignedTeacherIds = editForm.teachers
        .filter(teacherId => teacherId && teacherId.toString().trim() !== '')
        .map(teacherId => {
          // Handle both string and number IDs
          const id = typeof teacherId === 'string' ? parseInt(teacherId, 10) : teacherId;
          return isNaN(id) ? null : id;
        })
        .filter(id => id !== null);
      
      if (assignedTeacherIds.length > 0) {
        // Filter teachers to only include those assigned in Subjects & Teachers section
        const filtered = teachers.filter(teacher => 
          assignedTeacherIds.includes(teacher.id)
        );
        setEditFilteredTeachers(filtered);
      } else {
        setEditFilteredTeachers([]);
      }
    } else {
      setEditFilteredTeachers([]);
    }
  }, [editForm.teachers, teachers]);
  
  useEffect(() => {
    if (editForm.gradeId) {
      fetchSubjectsForEditGrade(parseInt(editForm.gradeId));
    }
  }, [editForm.gradeId]);

  const handleEditInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (editErrors[field]) {
      setEditErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleEditSectionChange = (index, value) => {
    setEditForm(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === index ? value : section
      )
    }));
    
    // Clear section errors when user starts typing
    if (editErrors[`section_${index}`]) {
      setEditErrors(prev => ({
        ...prev,
        [`section_${index}`]: ''
      }));
    }
  };

  const addEditSection = () => {
    setEditForm(prev => ({
      ...prev,
      sections: [...prev.sections, '']
    }));
  };

  const removeEditSection = (index) => {
    if (editForm.sections.length > 1) {
      setEditForm(prev => ({
        ...prev,
        sections: prev.sections.filter((_, i) => i !== index)
      }));
      
      // Clear error for removed section
      setEditErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`section_${index}`];
        return newErrors;
      });
    }
  };

  const handleEditSubjectChange = (index, value) => {
    setEditForm(prev => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) => 
        i === index ? value : subject
      )
    }));
    
    // Clear subject errors when user starts typing
    if (editErrors[`subject_${index}`]) {
      setEditErrors(prev => ({
        ...prev,
        [`subject_${index}`]: ''
      }));
    }
  };

  const addEditSubject = () => {
    setEditForm(prev => ({
      ...prev,
      subjects: [...prev.subjects, ''],
      teachers: [...prev.teachers, ''],
      subjectSections: [...prev.subjectSections, '']
    }));
  };

  const removeEditSubject = (index) => {
    if (editForm.subjects.length > 1) {
      setEditForm(prev => ({
        ...prev,
        subjects: prev.subjects.filter((_, i) => i !== index),
        teachers: prev.teachers.filter((_, i) => i !== index),
        subjectSections: prev.subjectSections.filter((_, i) => i !== index)
      }));
      
      // Clear error for removed subject
      setEditErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`subject_${index}`];
        delete newErrors[`teacher_${index}`];
        delete newErrors[`section_${index}`];
        return newErrors;
      });
    }
  };

  const handleEditTeacherChange = (index, value) => {
    console.log('Teacher change:', { index, value, currentTeachers: editForm.teachers, teachersLength: editForm.teachers.length });
    
    setEditForm(prev => {
      // Ensure teachers array is long enough
      const teachersArray = [...prev.teachers];
      while (teachersArray.length <= index) {
        teachersArray.push('');
      }
      
      const newTeachers = teachersArray.map((teacher, i) => 
        i === index ? value : teacher
      );
      console.log('New teachers array:', newTeachers);
      return {
        ...prev,
        teachers: newTeachers
      };
    });
    
    // Clear teacher errors when user starts typing
    if (editErrors[`teacher_${index}`]) {
      setEditErrors(prev => ({
        ...prev,
        [`teacher_${index}`]: ''
      }));
    }
  };

  const handleEditSubjectSectionChange = (index, value) => {
    console.log('Subject section change:', { index, value, currentSubjectSections: editForm.subjectSections });
    
    setEditForm(prev => {
      // Ensure subjectSections array is long enough
      const subjectSectionsArray = [...prev.subjectSections];
      while (subjectSectionsArray.length <= index) {
        subjectSectionsArray.push('');
      }
      
      const newSubjectSections = subjectSectionsArray.map((section, i) => 
        i === index ? value : section
      );
      
      console.log('New subject sections array:', newSubjectSections);
      
      return {
        ...prev,
        subjectSections: newSubjectSections
      };
    });
    
    // Clear error when user starts typing
    if (editErrors[`section_${index}`]) {
      setEditErrors(prev => ({
        ...prev,
        [`section_${index}`]: ''
      }));
    }
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.gradeId) {
      errors.gradeId = 'Class is required';
    }
    if (!editForm.academicYear) {
      errors.academicYear = 'Academic year is required';
    }
    if (!editForm.term) {
      errors.term = 'Term is required';
    }
    
    // Validate subjects
    const validSubjects = editForm.subjects.filter(subject => subject.trim() !== '');
    if (validSubjects.length === 0) {
      errors.subjects = 'At least one subject is required';
    }
    
    // Duplicate subjects are allowed - removed validation
    
    // Validate teachers
    const validTeachers = editForm.teachers.filter(teacher => teacher && teacher.toString().trim() !== '');
    if (validTeachers.length === 0) {
      errors.teachers = 'At least one teacher is required';
    }
    
    // Duplicate teachers are allowed for different subjects - removed validation
    editForm.subjectSections.forEach((section, index) => {
      if (editForm.subjects[index] && editForm.subjects[index].trim() !== '') {
        // Section is now optional - no validation required
        // if (!section.trim()) {
        //   errors[`section_${index}`] = 'Section is required for this subject';
        // }
      }
    });
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Filter out empty sections, subjects and teachers
      console.log('=== SAVING CLASS ===');
      console.log('Edit form sections (raw):', editForm.sections);
      console.log('Edit form streamId:', editForm.streamId);
      
      // If sections array is empty but streamId is set, get section name from stream
      let sectionsToSave = [...editForm.sections];
      if (sectionsToSave.length === 0 && editForm.streamId) {
        // First try to find in local streams array
        let selectedStream = streams.find(s => s.id.toString() === editForm.streamId.toString());
        
        // If not found locally, fetch from database
        if (!selectedStream) {
          try {
            const { data: streamData, error: streamError } = await supabase
              .from('streams')
              .select('name')
              .eq('id', editForm.streamId)
              .single();
            
            if (!streamError && streamData) {
              selectedStream = { id: editForm.streamId, name: streamData.name };
            }
          } catch (fetchErr) {
            console.error('Error fetching stream:', fetchErr);
          }
        }
        
        if (selectedStream && selectedStream.name) {
          sectionsToSave = [selectedStream.name];
          console.log('No sections in array, but streamId is set. Adding section from stream:', selectedStream.name);
        }
      }
      
      const validSections = sectionsToSave.filter(section => section && section.trim() !== '');
      const validSubjects = editForm.subjects.filter(subject => subject.trim() !== '');
      const validTeachers = editForm.teachers.filter(teacher => teacher && teacher.toString().trim() !== '');
      
      console.log('Valid sections to save:', validSections);
      
      // Prepare subject-sections mapping (sections are optional)
      // Use array index-based mapping to support duplicate subjects with different sections
      const subjectSectionsMapping = {};
      editForm.subjects.forEach((subject, index) => {
        if (subject.trim() !== '') {
          // Use index-based key to support duplicate subjects
          // Format: "subject_index" to allow same subject with different sections
          const key = `${subject.trim()}_${index}`;
          // Only add to mapping if section is provided (optional)
          if (editForm.subjectSections[index] && editForm.subjectSections[index].trim() !== '') {
            subjectSectionsMapping[key] = editForm.subjectSections[index].trim();
          }
          // If no section is provided, that's fine - it's optional
        }
      });

      // Auto-generate class name from grade and stream
      let generatedClassName = 'Class'; // Default fallback
      if (editForm.gradeId) {
        try {
          const { data: gradeData } = await supabase
            .from('grades')
            .select('name')
            .eq('id', editForm.gradeId)
            .single();
          
          if (editForm.streamId) {
            const { data: streamData } = await supabase
              .from('streams')
              .select('name')
              .eq('id', editForm.streamId)
              .single();
            
            if (gradeData?.name && streamData?.name) {
              generatedClassName = `${gradeData.name} - ${streamData.name}`;
            } else if (gradeData?.name) {
              generatedClassName = gradeData.name;
            } else {
              generatedClassName = `Grade ${editForm.gradeId} - Stream ${editForm.streamId}`;
            }
          } else if (gradeData?.name) {
            generatedClassName = gradeData.name;
          } else {
            generatedClassName = `Grade ${editForm.gradeId}`;
          }
        } catch (nameErr) {
          console.warn('Error fetching grade/stream names for class name generation:', nameErr);
          // If we can't fetch names, use a default format
          generatedClassName = editForm.streamId 
            ? `Grade ${editForm.gradeId} - Stream ${editForm.streamId}`
            : `Grade ${editForm.gradeId}`;
        }
      }

      const updateData = {
        class_name: generatedClassName,
        grade_id: editForm.gradeId ? parseInt(editForm.gradeId) : null,
        stream_id: editForm.streamId ? parseInt(editForm.streamId) : null,
        academic_year: editForm.academicYear ? parseInt(editForm.academicYear) : null,
        term: editForm.term ? parseInt(editForm.term) : null,
        class_teacher_id: editForm.classTeacherId ? parseInt(editForm.classTeacherId) : null,
        assistant_teacher_id: editForm.assistantTeacherId ? parseInt(editForm.assistantTeacherId) : null,
        sections: validSections, // Save the valid sections instead of empty array
        subjects: validSubjects,
        teachers: validTeachers,
        subject_sections: {},
        updated_at: new Date().toISOString()
      };
      
      console.log('Updating class data:', updateData);
      console.log('- Sections to save:', validSections);
      console.log('- Subjects to save:', validSubjects);
      console.log('- Teachers to save:', validTeachers);
      console.log('- Subject sections mapping to save:', subjectSectionsMapping);
      
      const { error } = await supabase
        .from('classes')
        .update(updateData)
        .eq('id', editingClass.id);
      
      if (error) {
        console.error('Error updating class:', error);
        alert(`Failed to update class: ${error.message || 'Please try again.'}`);
        setIsSubmitting(false);
        return;
      }
      
      console.log('Class updated successfully');
      
      // Verify the updated class data immediately after update
      const { data: updatedClassData, error: verifyError } = await supabase
        .from('classes')
        .select('*')
        .eq('id', editingClass.id)
        .single();
      
      if (updatedClassData) {
        console.log('=== VERIFICATION AFTER UPDATE ===');
        console.log('Verified updated class data from database:', updatedClassData);
        console.log('- Sections in DB:', updatedClassData.sections);
        console.log('- Sections type:', typeof updatedClassData.sections);
        console.log('- Is sections array?', Array.isArray(updatedClassData.sections));
        console.log('- Sections length:', updatedClassData.sections?.length);
        console.log('- Subjects in DB:', updatedClassData.subjects);
        console.log('=== END VERIFICATION ===');
      } else if (verifyError) {
        console.error('Error verifying updated class:', verifyError);
      }
      
      // Refetch classes from database to ensure we have the latest data including sections
      await fetchClasses();
      
      // Store updated class name for success popup
      setUpdatedClassName(generatedClassName);
      
      // Close edit popup
      setEditingClass(null);
      setEditForm({
        gradeId: '',
        streamId: '',
        academicYear: new Date().getFullYear().toString(),
        term: '',
        sections: [],
        subjects: [''],
        teachers: [''],
        subjectSections: [''],
        classTeacherId: '',
        assistantTeacherId: ''
      });
      setEditErrors({});
      
      // Show success popup
      setShowUpdateSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update class. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchiveClass = (cls) => {
    setClassToArchive(cls);
    setShowArchiveConfirmPopup(true);
  };

  const confirmArchiveClass = async () => {
    if (!classToArchive) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('classes')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', classToArchive.id);

      if (error) {
        console.error('Error archiving class:', error);
        alert('Failed to archive class. Please try again.');
        setIsSubmitting(false);
        setShowArchiveConfirmPopup(false);
        setClassToArchive(null);
        return;
      }

      // Store archived class name for success popup
      const archivedName = classToArchive.class_name;
      
      // Update the classes list
      setClasses(prev => prev.filter(c => c.id !== classToArchive.id));
      setShowArchiveConfirmPopup(false);
      setClassToArchive(null);
      
      // Show success popup
      setArchivedClassName(archivedName);
      setShowArchiveSuccessPopup(true);
    } catch (err) {
      console.error('Error archiving class:', err);
      alert('Failed to archive class. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelArchiveClass = () => {
    setShowArchiveConfirmPopup(false);
    setClassToArchive(null);
  };

  const handleRestoreClass = (cls) => {
    setClassToRestore(cls);
    setShowRestoreConfirmPopup(true);
  };

  const confirmRestoreClass = async () => {
    if (!classToRestore) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('classes')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('id', classToRestore.id);

      if (error) {
        console.error('Error restoring class:', error);
        alert('Failed to restore class. Please try again.');
        setIsSubmitting(false);
        setShowRestoreConfirmPopup(false);
        setClassToRestore(null);
        return;
      }

      // Store restored class name for success popup
      const restoredName = classToRestore.class_name;

      // Update the classes list - remove from archived view
      setClasses(prev => prev.filter(c => c.id !== classToRestore.id));
      setShowRestoreConfirmPopup(false);
      setClassToRestore(null);

      // Show success popup
      setRestoredClassName(restoredName);
      setShowRestoreSuccessPopup(true);
    } catch (err) {
      console.error('Error restoring class:', err);
      alert('Failed to restore class. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelRestoreClass = () => {
    setShowRestoreConfirmPopup(false);
    setClassToRestore(null);
  };

  const handleDeleteClass = (cls) => {
    setClassToDelete(cls);
    setShowDeleteConfirmPopup(true);
  };

  const confirmDeleteClass = async () => {
    if (!classToDelete) return;

    setIsSubmitting(true);
    try {
      // Permanently delete the class from the database
      const { error } = await supabase
        .from('classes')
        .delete()
        .eq('id', classToDelete.id);

      if (error) {
        console.error('Error deleting class:', error);
        alert('Failed to delete class. Please try again.');
        setIsSubmitting(false);
        setShowDeleteConfirmPopup(false);
        setClassToDelete(null);
        return;
      }

      // Remove from the classes list
      setClasses(prev => prev.filter(c => c.id !== classToDelete.id));
      
      // Store deleted class name for success popup
      setDeletedClassName(classToDelete.class_name);
      
      // Close confirmation popup
      setShowDeleteConfirmPopup(false);
      setClassToDelete(null);
      
      // Show beautiful success popup
      setShowDeleteSuccessPopup(true);
    } catch (err) {
      console.error('Error deleting class:', err);
      alert('Failed to delete class. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelDeleteClass = () => {
    setShowDeleteConfirmPopup(false);
    setClassToDelete(null);
  };

  const closeEditPopup = () => {
    setEditingClass(null);
    setEditForm({
      gradeId: '',
      streamId: '',
      academicYear: new Date().getFullYear().toString(),
      term: '',
      sections: [],
      subjects: [''],
      teachers: [''],
      subjectSections: [''],
      classTeacherId: '',
      assistantTeacherId: ''
    });
    setEditErrors({});
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">❌</div>
        <p>School data not found. Please login again.</p>
        <button onClick={() => navigate('/schoollogin')} className="submit-btn">
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="students-content">
          <div className="students-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1>Classes Management</h1>
                <p>View and manage all classes in your school</p>
              </div>
            </div>
          </div>

          <div className="table-toolbar">
            <input className="search-input" type="text" placeholder="Search class/grade..."
              value={searchTerm} onChange={(e)=>{setSearchTerm(e.target.value); setPage(1);}} />
            <select className="table-select" value={filterGrade} onChange={(e)=>{setFilterGrade(e.target.value); setPage(1);}}>
              <option value="all">All Grades</option>
              {grades.map(g => <option key={g.id} value={g.id.toString()}>{g.name}</option>)}
            </select>
            <select className="table-select" value={filterAcademicYear} onChange={(e)=>{setFilterAcademicYear(e.target.value); setPage(1);}}>
              <option value="all">All Years</option>
              {[...new Set(classes.map(c=>c.academic_year).filter(Boolean))].sort((a,b)=>b-a).map((y,i)=> <option key={i} value={y.toString()}>{y}</option>)}
            </select>
            <select className="table-select" value={filterTerm} onChange={(e)=>{setFilterTerm(e.target.value); setPage(1);}}>
              <option value="all">All Terms</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
              <option value="3">Term 3</option>
            </select>
            <select className="table-select" value={filterTeacherStatus} onChange={(e)=>{setFilterTeacherStatus(e.target.value); setPage(1);}}>
              <option value="all">All Teachers</option>
              <option value="active">Teacher Active</option>
              <option value="inactive">Teacher Inactive</option>
            </select>
            <select className="table-select" value={sortKey} onChange={(e)=>setSortKey(e.target.value)}>
              <option value="class">Sort: Class</option>
              <option value="grade">Sort: Grade</option>
              <option value="academic_year">Sort: Academic Year</option>
              <option value="term">Sort: Term</option>
              <option value="teacher">Sort: Class Teacher</option>
            </select>
            <select className="table-select" value={sortDir} onChange={(e)=>setSortDir(e.target.value)}>
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </div>

          <div className="students-stats">
            <div className="stat-item">
              <span className="stat-number">{classes.length}</span>
              <span className="stat-label">Total Classes</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {classes.reduce((total, cls) => total + (cls.subjects?.length || 0), 0)}
              </span>
              <span className="stat-label">Total Subjects</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {classes.reduce((total, cls) => total + (cls.teachers?.length || 0), 0)}
              </span>
              <span className="stat-label">Teacher Assignments</span>
            </div>
          </div>

          <div className="students-actions" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button 
              className="add-student-btn"
              onClick={() => navigate('/school/add-class')}
            >
              ➕ Add New Class
            </button>
            <div style={{ display: 'flex', gap: '0.5rem', background: '#F3F4F6', borderRadius: '8px', padding: '0.25rem' }}>
              <button
                onClick={() => setViewMode('active')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'active' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'active' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'active' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'active' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                📚 Active Classes
              </button>
              <button
                onClick={() => setViewMode('archived')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'archived' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'archived' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'archived' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'archived' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                📦 Archived Classes
              </button>
            </div>
          </div>

          {loadingClasses ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading classes...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">❌</div>
              <p>{error}</p>
              <button 
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : classes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📚</div>
              <h3>No Classes Found</h3>
              <p>You haven't created any classes yet. Start by adding your first class!</p>
              <button 
                className="add-first-class-btn"
                onClick={() => navigate('/school/add-class')}
              >
                ➕ Add Your First Class
              </button>
            </div>
          ) : (
            <div className="students-table-container">
              <table className="students-table">
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>Section</th>
                    <th>Class Teacher</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = getFilteredSorted();
                    return rows.map((cls) => (
                    <tr key={cls.id}>
                      <td>
                        {(() => {
                          // First check if grade object exists
                          if (cls.grade?.name) {
                            return cls.grade.name;
                          }
                          // If grade_id exists, try to find it in grades array
                          if (cls.grade_id) {
                            const grade = grades.find(g => g.id === cls.grade_id);
                            if (grade?.name) {
                              return grade.name;
                            }
                            return 'Loading...';
                          }
                          // No grade_id set - show "Not Set" to indicate it needs to be set
                          return 'Not Set';
                        })()}
                      </td>
                      <td>
                        {(() => {
                          // Handle sections - can be array, string, or null
                          if (!cls.sections) {
                            return 'N/A';
                          }
                          
                          let sectionsArray = [];
                          
                          // If it's already an array
                          if (Array.isArray(cls.sections)) {
                            sectionsArray = cls.sections.filter(s => s && s.trim() !== '');
                          }
                          // If it's a string, try to parse it
                          else if (typeof cls.sections === 'string') {
                            try {
                              const parsed = JSON.parse(cls.sections);
                              if (Array.isArray(parsed)) {
                                sectionsArray = parsed.filter(s => s && s.trim() !== '');
                              } else if (parsed && parsed.trim() !== '') {
                                sectionsArray = [parsed];
                              }
                            } catch (e) {
                              // If not JSON, treat as single section
                              if (cls.sections.trim() !== '') {
                                sectionsArray = [cls.sections];
                              }
                            }
                          }
                          
                          // Display sections
                          if (sectionsArray.length === 0) {
                            return 'N/A';
                          } else if (sectionsArray.length === 1) {
                            return sectionsArray[0];
                          } else {
                            return sectionsArray.join(', ');
                          }
                        })()}
                      </td>
                      <td>{(() => { const t = teachers.find(t=>t.id===cls.class_teacher_id); return t ? `${t.first_name} ${t.last_name}` : 'N/A'; })()}</td>
                      <td className="actions-cell students-actions-container">
                        <div className="action-buttons students-action-buttons">
                          {viewMode === 'active' ? (
                            <>
                              <button 
                                className="students-action-btn view-btn"
                                title="View Details"
                                onClick={() => handleViewDetails(cls)}
                                disabled={isSubmitting}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                <span>View</span>
                              </button>
                              <button 
                                className="students-action-btn edit-btn"
                                title="Edit Class"
                                onClick={() => handleEditClass(cls)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                <span>Edit</span>
                              </button>
                              <button 
                                className="students-action-btn archive-btn"
                                title="Archive Class"
                                onClick={() => handleArchiveClass(cls)}
                                disabled={isSubmitting}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                </svg>
                                <span>Archive</span>
                              </button>
                              <button 
                                className="students-action-btn delete-btn"
                                title="Delete Class"
                                onClick={() => handleDeleteClass(cls)}
                                disabled={isSubmitting}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  <line x1="10" y1="11" x2="10" y2="17"></line>
                                  <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                                <span>Delete</span>
                              </button>
                            </>
                          ) : (
                            <button 
                              className="students-action-btn restore-btn"
                              title="Restore Class"
                              onClick={() => handleRestoreClass(cls)}
                              disabled={isSubmitting}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                              </svg>
                              <span>Restore</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ));
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Class Details Popup */}
      {showDetailsPopup && selectedClassDetails && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in',
            padding: '1rem'
          }}
          onClick={() => {
            setShowDetailsPopup(false);
            setSelectedClassDetails(null);
          }}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
              borderRadius: '24px',
              padding: '0',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Section with Gradient */}
            <div style={{
              background: 'linear-gradient(135deg, #FF8B3D 0%, #E67A35 100%)',
              padding: '2rem 2.5rem',
              borderRadius: '24px 24px 0 0',
              position: 'relative',
              color: '#FFFFFF'
            }}>
              {/* Close Button */}
              <button
                onClick={() => {
                  setShowDetailsPopup(false);
                  setSelectedClassDetails(null);
                }}
                style={{
                  position: 'absolute',
                  top: '1.5rem',
                  right: '1.5rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s',
                  fontWeight: 'bold',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'rotate(90deg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }}
              >
                ×
              </button>

              {/* Header Content */}
              <div style={{ textAlign: 'center' }}>
                <h2 style={{
                  color: '#FFFFFF',
                  fontSize: '2.25rem',
                  fontWeight: '700',
                  marginBottom: '0.75rem',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  {(() => {
                    const gradeName = selectedClassDetails.grade?.name || 'Grade';
                    const streamName = selectedClassDetails.stream?.name;
                    return streamName ? `${gradeName} - ${streamName}` : gradeName;
                  })()}
                </h2>
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    background: selectedClassDetails.is_active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    color: '#FFFFFF',
                    padding: '0.4rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    border: `1px solid ${selectedClassDetails.is_active ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`
                  }}>
                    {selectedClassDetails.is_active ? '✓ Active' : '✗ Inactive'}
                  </span>
                  <span style={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span>📅</span>
                    Created: {new Date(selectedClassDetails.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div style={{
              padding: '2.5rem',
              overflowY: 'auto',
              flex: 1
            }}>

            {/* Details Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              {/* Basic Information */}
              <div style={{
                background: 'linear-gradient(135deg, #F9FAFB 0%, #FFFFFF 100%)',
                borderRadius: '16px',
                padding: '1.5rem',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
              }}>
                <h3 style={{
                  color: '#1F2937',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  marginBottom: '1.25rem',
                  borderBottom: '3px solid #FF8B3D',
                  paddingBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>📋</span> Basic Information
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{
                    background: '#FFFFFF',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #F3F4F6'
                  }}>
                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Grade</span>
                    <div style={{ color: '#1F2937', fontWeight: '700', marginTop: '0.5rem', fontSize: '1.1rem' }}>
                      {selectedClassDetails.grade?.name || 'N/A'}
                    </div>
                  </div>
                  {selectedClassDetails.stream && (
                    <div style={{
                      background: '#FFFFFF',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #F3F4F6'
                    }}>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Stream</span>
                      <div style={{ color: '#1F2937', fontWeight: '700', marginTop: '0.5rem', fontSize: '1.1rem' }}>
                        {selectedClassDetails.stream?.name || 'N/A'}
                      </div>
                    </div>
                  )}
                  <div style={{
                    background: '#FFFFFF',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #F3F4F6'
                  }}>
                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Academic Year</span>
                    <div style={{ color: '#1F2937', fontWeight: '700', marginTop: '0.5rem', fontSize: '1.1rem' }}>
                      {selectedClassDetails.academic_year || 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    background: '#FFFFFF',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #F3F4F6'
                  }}>
                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Term</span>
                    <div style={{ color: '#1F2937', fontWeight: '700', marginTop: '0.5rem', fontSize: '1.1rem' }}>
                      {selectedClassDetails.term ? `Term ${selectedClassDetails.term}` : 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    background: '#FFFFFF',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #F3F4F6'
                  }}>
                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Class Teacher</span>
                    <div style={{ color: '#1F2937', fontWeight: '700', marginTop: '0.5rem', fontSize: '1.1rem' }}>
                      {(() => {
                        const teacher = teachers.find(t => t.id === selectedClassDetails.class_teacher_id);
                        return teacher ? `${teacher.first_name} ${teacher.last_name}` : 'N/A';
                      })()}
                    </div>
                  </div>
                  {selectedClassDetails.assistant_teacher_id && (
                    <div style={{
                      background: '#FFFFFF',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #F3F4F6'
                    }}>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Assistant Teacher</span>
                      <div style={{ color: '#1F2937', fontWeight: '700', marginTop: '0.5rem', fontSize: '1.1rem' }}>
                        {(() => {
                          const teacher = teachers.find(t => t.id === selectedClassDetails.assistant_teacher_id);
                          return teacher ? `${teacher.first_name} ${teacher.last_name}` : 'N/A';
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics */}
              <div style={{
                background: 'linear-gradient(135deg, #F9FAFB 0%, #FFFFFF 100%)',
                borderRadius: '16px',
                padding: '1.5rem',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
              }}>
                <h3 style={{
                  color: '#1F2937',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  marginBottom: '1.25rem',
                  borderBottom: '3px solid #10B981',
                  paddingBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>📊</span> Statistics
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div style={{
                    background: '#FFFFFF',
                    padding: '1rem',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Students</div>
                    <div style={{ color: '#1F2937', fontWeight: '700', fontSize: '1.75rem' }}>
                      {studentCounts[selectedClassDetails.id] || 0}
                    </div>
                  </div>
                  <div style={{
                    background: '#FFFFFF',
                    padding: '1rem',
                    borderRadius: '12px',
                    border: '2px solid #10B981',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Present Today</div>
                    <div style={{ color: '#10B981', fontWeight: '700', fontSize: '1.75rem' }}>
                      {todayAttendanceCounts[selectedClassDetails.id] || 0}
                    </div>
                  </div>
                  <div style={{
                    background: '#FFFFFF',
                    padding: '1rem',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Subjects</div>
                    <div style={{ color: '#1F2937', fontWeight: '700', fontSize: '1.75rem' }}>
                      {selectedClassDetails.subjects?.length || 0}
                    </div>
                  </div>
                  <div style={{
                    background: '#FFFFFF',
                    padding: '1rem',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#6B7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Teachers</div>
                    <div style={{ color: '#1F2937', fontWeight: '700', fontSize: '1.75rem' }}>
                      {selectedClassDetails.teachers?.length || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Subjects Section */}
            <div style={{
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #F9FAFB 0%, #FFFFFF 100%)',
              borderRadius: '16px',
              padding: '1.5rem',
              border: '1px solid #E5E7EB',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
            }}>
                <h3 style={{
                  color: '#1F2937',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  marginBottom: '1.25rem',
                  borderBottom: '3px solid #8B5CF6',
                  paddingBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>📚</span> Subjects & Teachers
                </h3>
              {selectedClassDetails.subjects && selectedClassDetails.subjects.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {selectedClassDetails.subjects.map((subject, index) => {
                    const teacherId = selectedClassDetails.teachers?.[index];
                    const teacher = teacherId ? teachers.find(t => t.id === parseInt(teacherId)) : null;
                    const section = selectedClassDetails.subject_sections?.[subject];
                    
                    return (
                      <div key={index} style={{
                        background: '#FFFFFF',
                        borderRadius: '8px',
                        padding: '1rem',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem'
                        }}>
                          <span style={{
                            background: 'linear-gradient(135deg, #FF8B3D 0%, #E67A35 100%)',
                            color: '#FFFFFF',
                            padding: '0.5rem 1rem',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '0.95rem'
                          }}>
                            {subject}
                          </span>
                          {section && (
                            <span style={{
                              background: '#F3F4F6',
                              color: '#374151',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              fontWeight: '500'
                            }}>
                              Section: {section}
                            </span>
                          )}
                        </div>
                        {teacher ? (
                          <div style={{
                            color: '#6B7280',
                            fontSize: '0.9rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <span>👤</span>
                            <span style={{ color: '#10B981', fontWeight: '500' }}>
                              {teacher.first_name} {teacher.last_name}
                            </span>
                          </div>
                        ) : (
                          <div style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
                            No teacher assigned
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: '#9CA3AF', textAlign: 'center', padding: '1rem' }}>
                  No subjects assigned
                </div>
              )}
            </div>

            {/* Sections */}
            {selectedClassDetails.sections && selectedClassDetails.sections.length > 0 && (
              <div style={{
                marginBottom: '1.5rem',
                background: 'linear-gradient(135deg, #F9FAFB 0%, #FFFFFF 100%)',
                borderRadius: '16px',
                padding: '1.5rem',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
              }}>
                <h3 style={{
                  color: '#1F2937',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  marginBottom: '1.25rem',
                  borderBottom: '3px solid #3B82F6',
                  paddingBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>📑</span> Sections
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {selectedClassDetails.sections.map((section, index) => (
                    <span key={index} style={{
                      background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                      color: '#FFFFFF',
                      padding: '0.6rem 1.25rem',
                      borderRadius: '12px',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                      transition: 'all 0.2s'
                    }}>
                      {section}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Grading Matrix */}
            {selectedClassDetails.grading_matrix && (
              <div style={{
                marginBottom: '1.5rem'
              }}>
                <h3 style={{
                  color: '#1F2937',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  borderBottom: '2px solid #E5E7EB',
                  paddingBottom: '0.5rem'
                }}>
                  Grading Matrix
                </h3>
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB',
                  color: '#374151',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem'
                }}>
                  {typeof selectedClassDetails.grading_matrix === 'string' 
                    ? selectedClassDetails.grading_matrix 
                    : JSON.stringify(selectedClassDetails.grading_matrix, null, 2)}
                </div>
              </div>
            )}

            </div>

            {/* Action Buttons Footer */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
              padding: '1.5rem 2.5rem',
              background: '#F9FAFB',
              borderTop: '1px solid #E5E7EB',
              borderRadius: '0 0 24px 24px'
            }}>
              <button
                onClick={() => {
                  setShowDetailsPopup(false);
                  setSelectedClassDetails(null);
                  handleEditClass(selectedClassDetails);
                }}
                style={{
                  background: 'linear-gradient(135deg, #FF8B3D 0%, #E67A35 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '0.875rem 2.25rem',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 139, 61, 0.3)',
                  transition: 'all 0.3s ease',
                  letterSpacing: '0.3px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 139, 61, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 139, 61, 0.3)';
                }}
              >
                ✏️ Edit Class
              </button>
              <button
                onClick={() => {
                  setShowDetailsPopup(false);
                  setSelectedClassDetails(null);
                }}
                style={{
                  background: '#FFFFFF',
                  color: '#374151',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  padding: '0.875rem 2.25rem',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  letterSpacing: '0.3px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#F3F4F6';
                  e.currentTarget.style.borderColor = '#D1D5DB';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                }}
              >
                ✕ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Class Popup */}
      {editingClass && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="popup-header">
              <h3>Edit Class</h3>
              <button className="close-btn" onClick={closeEditPopup}>
                ×
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="edit-class-form">
              <div className="form-section">
                <h3>Class Information</h3>
                
                {/* Grade and Section */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="editGradeId">Class *</label>
                    <select
                      id="editGradeId"
                      value={editForm.gradeId}
                      onChange={(e) => {
                        handleEditInputChange('gradeId', e.target.value);
                        handleEditInputChange('streamId', ''); // Reset stream when grade changes
                        handleEditInputChange('sections', []); // Reset sections when grade changes
                        handleEditInputChange('subjects', ['']); // Reset subjects
                        handleEditInputChange('teachers', ['']);
                      }}
                      className={editErrors.gradeId ? 'error' : ''}
                    >
                      <option value="">-- Select Class --</option>
                      {grades.map(grade => (
                        <option key={grade.id} value={grade.id.toString()}>
                          {grade.name}
                        </option>
                      ))}
                    </select>
                    {editErrors.gradeId && (
                      <span className="error-text">{editErrors.gradeId}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="editStreamId">Section</label>
                    <select
                      id="editStreamId"
                      value={editForm.streamId}
                      onChange={(e) => {
                        const selectedStreamId = e.target.value;
                        const selectedStream = streams.find(s => s.id.toString() === selectedStreamId);
                        
                        setEditForm(prev => {
                          // If a section is selected, add it to the sections array
                          // If no section is selected, clear the sections array
                          let updatedSections = [];
                          if (selectedStream && selectedStream.name) {
                            // Add the selected section name to the sections array
                            updatedSections = [selectedStream.name];
                          }
                          
                          return {
                            ...prev,
                            streamId: selectedStreamId,
                            sections: updatedSections
                          };
                        });
                      }}
                      disabled={!editForm.gradeId}
                    >
                      <option value="">-- Select Section (Optional) --</option>
                      {streams.filter(s => s.grade_id === parseInt(editForm.gradeId)).map(stream => (
                        <option key={stream.id} value={stream.id.toString()}>
                          {stream.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Academic Year and Term */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="editAcademicYear">Academic Year *</label>
                    <select
                      id="editAcademicYear"
                      value={editForm.academicYear}
                      onChange={(e) => handleEditInputChange('academicYear', e.target.value)}
                      className={editErrors.academicYear ? 'error' : ''}
                    >
                      {(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        for (let i = currentYear - 2; i <= currentYear + 5; i++) {
                          years.push(i.toString());
                        }
                        return years;
                      })().map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    {editErrors.academicYear && (
                      <span className="error-text">{editErrors.academicYear}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="editTerm">Term *</label>
                    <select
                      id="editTerm"
                      value={editForm.term}
                      onChange={(e) => handleEditInputChange('term', e.target.value)}
                      className={editErrors.term ? 'error' : ''}
                    >
                      <option value="">-- Select Term --</option>
                      <option value="1">Term 1</option>
                      <option value="2">Term 2</option>
                      <option value="3">Term 3</option>
                    </select>
                    {editErrors.term && (
                      <span className="error-text">{editErrors.term}</span>
                    )}
                  </div>
                </div>
                
              </div>
              
              <div className="form-section">
                <div className="subjects-header">
                  <h4>Subjects & Teachers *</h4>
                  <button 
                    type="button" 
                    className="add-subject-btn"
                    onClick={addEditSubject}
                  >
                    + Add Subject
                  </button>
                </div>
                
                {editErrors.subjects && (
                  <span className="error-text">{editErrors.subjects}</span>
                )}
                
                {editErrors.teachers && (
                  <span className="error-text">{editErrors.teachers}</span>
                )}
                
                <div className="subjects-list">
                  {editForm.subjects.map((subject, index) => (
                    <div key={index} className="subject-teacher-item">
                      <div className="subject-input-group">
                        <select
                          value={subject}
                          onChange={(e) => handleEditSubjectChange(index, e.target.value)}
                          className={editErrors[`subject_${index}`] ? 'error' : ''}
                          disabled={!editForm.gradeId}
                        >
                          <option value="">
                            {!editForm.gradeId 
                              ? 'Select class first' 
                              : editAvailableSubjects.length === 0
                              ? 'No subjects found for this class'
                              : 'Select Subject'}
                          </option>
                          {editAvailableSubjects.length > 0 && editAvailableSubjects.map((subjectName, idx) => (
                            <option key={idx} value={subjectName}>
                              {subjectName}
                            </option>
                          ))}
                        </select>
                        {editErrors[`subject_${index}`] && (
                          <span className="error-text">{editErrors[`subject_${index}`]}</span>
                        )}
                      </div>
                      
                      <div className="teacher-input-group">
                        <select
                          value={editForm.teachers[index] ? editForm.teachers[index].toString() : ''}
                          onChange={(e) => {
                            console.log('Select change:', { index, value: e.target.value, teachers: editForm.teachers });
                            handleEditTeacherChange(index, e.target.value);
                          }}
                          className={editErrors[`teacher_${index}`] ? 'error' : ''}
                          disabled={!editForm.subjects[index] || editForm.subjects[index].trim() === ''}
                        >
                          <option value="">
                            {!editForm.subjects[index] || editForm.subjects[index].trim() === '' 
                              ? 'Select subject first' 
                              : loadingTeachers 
                              ? 'Loading teachers...' 
                              : editFilteredTeachers.length === 0
                              ? 'No teachers found for this subject'
                              : 'Select Teacher'}
                          </option>
                          {editFilteredTeachers.map((teacher) => (
                            <option key={teacher.id} value={teacher.id.toString()}>
                              {teacher.first_name} {teacher.last_name}
                            </option>
                          ))}
                        </select>
                        {editErrors[`teacher_${index}`] && (
                          <span className="error-text">{editErrors[`teacher_${index}`]}</span>
                        )}
                      </div>
                      
                      {editForm.subjects.length > 1 && (
                        <button
                          type="button"
                          className="remove-subject-btn"
                          onClick={() => removeEditSubject(index)}
                          title="Remove subject and teacher"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Class Teacher and Assistant Teacher - After Subjects/Teachers */}
              <div className="form-section">
                <h3>Class Teachers</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="editClassTeacherId">Class Teacher</label>
                    <select
                      id="editClassTeacherId"
                      value={editForm.classTeacherId || ''}
                      onChange={(e) => handleEditInputChange('classTeacherId', e.target.value)}
                      disabled={editFilteredTeachers.length === 0}
                    >
                      <option value="">
                        {editFilteredTeachers.length === 0 
                          ? 'Assign teachers in Subjects & Teachers section first' 
                          : 'Select Class Teacher'}
                      </option>
                      {editFilteredTeachers.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.first_name} {t.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="editAssistantTeacherId">Assistant Teacher</label>
                    <select
                      id="editAssistantTeacherId"
                      value={editForm.assistantTeacherId || ''}
                      onChange={(e) => handleEditInputChange('assistantTeacherId', e.target.value)}
                      disabled={editFilteredTeachers.length === 0}
                    >
                      <option value="">
                        {editFilteredTeachers.length === 0 
                          ? 'Assign teachers in Subjects & Teachers section first' 
                          : 'Select Assistant Teacher'}
                      </option>
                      {editFilteredTeachers.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.first_name} {t.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={closeEditPopup}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmPopup && classToDelete && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelDeleteClass}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Delete Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>🗑️</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Delete Class?
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              Are you sure you want to permanently delete <strong style={{ color: '#1F2937' }}>"{classToDelete.class_name}"</strong>?
            </p>

            <div style={{
              background: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '2rem',
              textAlign: 'left',
              position: 'relative',
              zIndex: 1
            }}>
              <p style={{
                color: '#991B1B',
                fontSize: '0.9rem',
                margin: 0,
                marginBottom: '0.75rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                fontWeight: '600'
              }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <span>This action CANNOT be undone!</span>
              </p>
              <ul style={{
                color: '#991B1B',
                fontSize: '0.85rem',
                margin: 0,
                paddingLeft: '1.5rem',
                lineHeight: '1.8'
              }}>
                <li>Class will be permanently removed from the database</li>
                <li>All class data will be deleted</li>
                <li>This action is irreversible</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={cancelDeleteClass}
                disabled={isSubmitting}
                style={{
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.background = '#E5E7EB';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.background = '#F3F4F6';
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteClass}
                disabled={isSubmitting}
                style={{
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                  }
                }}
              >
                {isSubmitting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Popup */}
      {showArchiveConfirmPopup && classToArchive && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelArchiveClass}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Archive Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>📦</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Archive Class?
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              Are you sure you want to archive <strong style={{ color: '#1F2937' }}>"{classToArchive.class_name}"</strong>?
            </p>

            <div style={{
              background: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '2rem',
              textAlign: 'left',
              position: 'relative',
              zIndex: 1
            }}>
              <p style={{
                color: '#92400E',
                fontSize: '0.9rem',
                margin: 0,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
                <span>This will mark the class as inactive and hide it from the active classes list. You can view and restore archived classes later.</span>
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={cancelArchiveClass}
                disabled={isSubmitting}
                style={{
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.background = '#E5E7EB';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.background = '#F3F4F6';
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmArchiveClass}
                disabled={isSubmitting}
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
                  }
                }}
              >
                {isSubmitting ? 'Archiving...' : 'Archive Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Success Popup */}
      {showArchiveSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowArchiveSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>📦</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Class Archived Successfully! ✅
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              The class <strong style={{ color: '#1F2937' }}>"{archivedClassName}"</strong> has been archived successfully.
            </p>

            <div style={{
              background: '#F3F4F6',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '2rem',
              textAlign: 'left',
              position: 'relative',
              zIndex: 1
            }}>
              <p style={{
                color: '#6B7280',
                fontSize: '0.9rem',
                margin: 0,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
                <span>You can view and restore archived classes by switching to the "Archived Classes" view.</span>
              </p>
            </div>

            {/* Action Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={() => setShowArchiveSuccessPopup(false)}
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Popup */}
      {showRestoreConfirmPopup && classToRestore && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelRestoreClass}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Restore Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>♻️</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Restore Class?
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              Are you sure you want to restore <strong style={{ color: '#1F2937' }}>"{classToRestore.class_name}"</strong>?
            </p>

            <div style={{
              background: '#ECFDF5',
              border: '1px solid #A7F3D0',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '2rem',
              textAlign: 'left',
              position: 'relative',
              zIndex: 1
            }}>
              <p style={{
                color: '#065F46',
                fontSize: '0.9rem',
                margin: 0,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
                <span>This will mark the class as active and make it visible in the active classes list again.</span>
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={cancelRestoreClass}
                disabled={isSubmitting}
                style={{
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.background = '#E5E7EB';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.background = '#F3F4F6';
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRestoreClass}
                disabled={isSubmitting}
                style={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }
                }}
              >
                {isSubmitting ? 'Restoring...' : 'Restore Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Success Popup */}
      {showRestoreSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowRestoreSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Class Restored Successfully! 🎉
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              The class <strong style={{ color: '#1F2937' }}>"{restoredClassName}"</strong> has been restored successfully.
            </p>

            {/* Action Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={() => setShowRestoreSuccessPopup(false)}
                style={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Success Popup */}
      {showUpdateSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowUpdateSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Class Updated Successfully! 🎉
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              The class <strong style={{ color: '#1F2937' }}>"{updatedClassName}"</strong> has been updated successfully.
            </p>

            {/* Action Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={() => setShowUpdateSuccessPopup(false)}
                style={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Popup */}
      {showDeleteSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => {
            setShowDeleteSuccessPopup(false);
            setDeletedClassName('');
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Class Deleted Successfully! 🗑️
            </h2>

            {/* Subtitle */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              position: 'relative',
              zIndex: 1
            }}>
              The class has been permanently removed from the database.
            </p>

            {/* Class Details */}
            <div style={{
              background: '#FEE2E2',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                textAlign: 'left'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#991B1B', fontWeight: '500' }}>Deleted Class:</span>
                  <span style={{ color: '#1F2937', fontWeight: '600' }}>{deletedClassName}</span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={() => {
                  setShowDeleteSuccessPopup(false);
                  setDeletedClassName('');
                }}
                style={{
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  transition: 'all 0.3s ease',
                  flex: 1,
                  maxWidth: '200px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default Classes;
